import React, { useEffect, useState, useRef } from "react";
import {
	CircularProgress,
	Dialog,
	DialogActions,
} from "@material-ui/core";
import { decrypt } from '../../shared/utilities/decrypt.js'
import { api } from "../../shared/utilities/api";
import apiConfigs from "../../shared/utilities/apiConfigs";
import ConfirmationDialog from '../../shared/ConfirmationDialog';
import CustomAlert from "./CustomAlert";
import useStore from "../../shared/store";

const contactInquiryTypes = [
	{ key: "message_clinic", label: "Message Clinic" },
	{ key: "reschedule_or_cancel", label: "Reschedule Appointment" },
	{ key: "missed_appointment", label: "Missed Appointment" },
	{ key: "doctor_not_joined", label: "Doctor Not Available for Video Call" },
];

// ─── Font injection ────────────────────────────────────────────────────────────
const injectFont = () => {
	if (document.getElementById('tech-support-font')) return;
	const link = document.createElement('link');
	link.id = 'tech-support-font';
	link.rel = 'stylesheet';
	link.href = 'https://fonts.googleapis.com/css2?family=Manrope:wght@600;700;800&family=Inter:wght@400;500;600&display=swap';
	document.head.appendChild(link);
};

// ─── Inline styles ─────────────────────────────────────────────────────────────
const injectStyles = () => {
	if (document.getElementById('tech-support-styles')) return;
	const style = document.createElement('style');
	style.id = 'tech-support-styles';
	style.textContent = `
		.ts-wrap, .ts-wrap *, .ts-wrap *::before, .ts-wrap *::after {
			box-sizing: border-box;
		}
		.ts-wrap {
			font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
			width: 100%;
		}

		/* ── Collapsible card ── */
		.ts-card {
			width: 100%;
			background: #fff;
			border-radius: 16px;
			border: 1px solid rgba(0,0,0,0.07);
			box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 8px 24px -8px rgba(0,0,0,0.08);
			overflow: hidden;
		}
		.ts-card__head {
			padding: 16px 20px;
			display: flex;
			align-items: center;
			justify-content: space-between;
			cursor: pointer;
			transition: background 0.15s;
		}
		.ts-card__head:hover { background: #FAFAFA; }
		.ts-card__head-left { display: flex; align-items: center; gap: 12px; }
		.ts-card__icon {
			width: 38px; height: 38px;
			border-radius: 10px;
			background: #FEF0E7;
			color: #E47029;
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
		}
		.ts-card__title {
			font-family: 'Manrope', sans-serif;
			font-size: 15px;
			font-weight: 700;
			color: #18181A;
		}
		.ts-card__chevron {
			width: 30px; height: 30px;
			border-radius: 8px;
			display: flex;
			align-items: center;
			justify-content: center;
			color: #9A9A9F;
			transition: transform 0.25s ease, background 0.15s;
			flex-shrink: 0;
			background: none;
			border: none;
			cursor: pointer;
		}
		.ts-card__head:hover .ts-card__chevron { background: #F5F3F0; }
		.ts-card__chevron.expanded { transform: rotate(180deg); }

		.ts-card__body {
			padding: 0 20px 20px;
			border-top: 1px solid rgba(0,0,0,0.06);
			animation: ts-slideDown 0.25s ease;
		}
		@keyframes ts-slideDown {
			from { opacity: 0; transform: translateY(-6px); }
			to { opacity: 1; transform: translateY(0); }
		}
		.ts-card__desc {
			font-size: 13px;
			color: #6B6760;
			line-height: 1.6;
			margin: 16px 0;
		}
		.ts-card__cta {
			display: inline-flex;
			align-items: center;
			gap: 8px;
			padding: 10px 20px;
			border-radius: 10px;
			background: #E47029;
			color: #fff;
			border: none;
			font-size: 13px;
			font-weight: 700;
			cursor: pointer;
			font-family: inherit;
			transition: background 0.15s;
		}
		.ts-card__cta:hover { background: #B94D1A; }

		/* ── Dialog ── */
		.ts-dialog-head {
			background: linear-gradient(135deg, #F4A96A 0%, #E47029 100%);
			padding: 20px 24px;
			position: relative;
			overflow: hidden;
		}
		.ts-dialog-head::after {
			content: '';
			position: absolute;
			width: 120px; height: 120px;
			border-radius: 50%;
			border: 1px solid rgba(255,255,255,0.14);
			right: -30px; top: -40px;
		}
		.ts-dialog-title {
			font-family: 'Manrope', sans-serif;
			font-size: 17px;
			font-weight: 800;
			color: #fff;
			position: relative;
			z-index: 1;
		}
		.ts-dialog-sub {
			font-size: 12px;
			color: rgba(255,255,255,0.85);
			margin-top: 4px;
			position: relative;
			z-index: 1;
			line-height: 1.5;
		}

		.ts-dialog-body { padding: 20px 24px; max-height: 480px; overflow-y: auto; }

		.ts-field { margin-bottom: 16px; }
		.ts-field label {
			display: block;
			font-size: 11.5px;
			font-weight: 600;
			color: #5B5B60;
			text-transform: uppercase;
			letter-spacing: 0.04em;
			margin-bottom: 7px;
		}
		.ts-field input, .ts-field select, .ts-field textarea {
			width: 100%;
			padding: 11px 14px;
			border: 1.5px solid rgba(0,0,0,0.11);
			border-radius: 10px;
			font-size: 14px;
			font-family: inherit;
			color: #18181A;
			background: #FAFAFA;
			transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
		}
		.ts-field input:hover, .ts-field select:hover, .ts-field textarea:hover { border-color: #c2bdb3; }
		.ts-field input:focus, .ts-field select:focus, .ts-field textarea:focus {
			outline: none;
			border-color: #E47029;
			box-shadow: 0 0 0 3px rgba(228,112,41,0.12);
			background: #fff;
		}
		.ts-field input.error, .ts-field select.error, .ts-field textarea.error { border-color: #E53E3E; }
		.ts-field textarea { resize: vertical; min-height: 90px; }
		.ts-error-text { font-size: 11px; color: #E53E3E; margin-top: 4px; }
		.ts-char-count { font-size: 11px; color: #C0BDB8; text-align: right; margin-top: 4px; }

		.ts-upload-zone {
			border: 2px dashed rgba(228,112,41,0.3);
			border-radius: 10px;
			padding: 14px;
			text-align: center;
			background: #FDFAF8;
			cursor: pointer;
			transition: border-color 0.15s, background 0.15s;
			position: relative;
		}
		.ts-upload-zone:hover { border-color: #E47029; background: #FEF0E7; }
		.ts-upload-zone input[type="file"] {
			position: absolute;
			inset: 0;
			opacity: 0;
			cursor: pointer;
			width: 100%;
			height: 100%;
		}
		.ts-upload-zone__text { font-size: 12.5px; color: #E47029; font-weight: 600; }
		.ts-upload-hint { font-size: 11px; color: #C0BDB8; margin-top: 6px; }

		.ts-attached-file {
			display: flex;
			align-items: center;
			justify-content: space-between;
			margin-top: 10px;
			padding: 9px 12px;
			background: #FAFAFA;
			border: 1px solid rgba(0,0,0,0.07);
			border-radius: 10px;
		}
		.ts-attached-file__left {
			display: flex;
			align-items: center;
			gap: 8px;
			font-size: 12.5px;
			color: #18181A;
			overflow: hidden;
		}
		.ts-attached-file__left span {
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
		.ts-clear-icon {
			color: #C0BDB8;
			cursor: pointer;
			flex-shrink: 0;
			transition: color 0.15s;
		}
		.ts-clear-icon:hover { color: #E53E3E; }

		.ts-no-email-msg {
			margin: 28px 24px;
			line-height: 1.6;
			font-size: 14px;
			color: #6B6760;
		}

		/* ── Dialog footer ── */
		.ts-dialog-foot {
			padding: 14px 24px 20px;
			display: flex;
			gap: 10px;
			border-top: 1px solid rgba(0,0,0,0.06);
		}
		.ts-btn-cancel {
			flex: 1;
			padding: 11px;
			border-radius: 10px;
			border: 1.5px solid #E47029;
			background: transparent;
			color: #E47029;
			font-size: 13px;
			font-weight: 700;
			cursor: pointer;
			font-family: inherit;
			transition: background 0.15s;
		}
		.ts-btn-cancel:hover:not(:disabled) { background: rgba(228,112,41,0.08); }
		.ts-btn-cancel:disabled { opacity: 0.5; cursor: not-allowed; }
		.ts-btn-send {
			flex: 1;
			padding: 11px;
			border-radius: 10px;
			border: none;
			background: #E47029;
			color: #fff;
			font-size: 13px;
			font-weight: 700;
			cursor: pointer;
			font-family: inherit;
			transition: background 0.15s;
		}
		.ts-btn-send:hover:not(:disabled) { background: #B94D1A; }
		.ts-btn-send:disabled { opacity: 0.5; cursor: not-allowed; }

		.ts-loading-wrap {
			width: 100%;
			display: flex;
			justify-content: center;
			padding: 10px 0;
		}
	`;
	document.head.appendChild(style);
};

// ─── Icons ─────────────────────────────────────────────────────────────────────
const MessageIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18" aria-hidden="true">
		<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
	</svg>
);

const MailSendIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" aria-hidden="true">
		<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
		<polyline points="22,6 12,13 2,6" />
	</svg>
);

const ChevronIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16" aria-hidden="true">
		<polyline points="6 9 12 15 18 9" />
	</svg>
);

const AttachIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" aria-hidden="true">
		<path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
	</svg>
);

const ClearIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" aria-hidden="true">
		<line x1="18" y1="6" x2="6" y2="18" />
		<line x1="6" y1="6" x2="18" y2="18" />
	</svg>
);

// ─── Reusable field component ──────────────────────────────────────────────────
const StyledField = ({
	id, label, value, placeholder, multiline, rows, onChange,
	error, helperText, showTextCounter, maxLength, type = 'text',
	options, ...restProps
}) => {
	const isSelect = !!options;

	return (
		<div className="ts-field">
			<label htmlFor={id}>{label}</label>
			{multiline ? (
				<textarea
					id={id}
					placeholder={placeholder}
					value={value}
					onChange={onChange}
					rows={rows}
					maxLength={maxLength}
					className={error ? 'error' : ''}
					{...restProps}
				/>
			) : isSelect ? (
				<select
					id={id}
					value={value}
					onChange={onChange}
					className={error ? 'error' : ''}
					{...restProps}
				>
					{options}
				</select>
			) : (
				<input
					id={id}
					type={type}
					placeholder={placeholder}
					value={value}
					onChange={onChange}
					className={error ? 'error' : ''}
					{...restProps}
				/>
			)}
			{error && helperText && <div className="ts-error-text">{helperText}</div>}
			{showTextCounter && (
				<div className="ts-char-count">{value ? value.length : 0}/{maxLength}</div>
			)}
		</div>
	);
};

// ─── Main component ────────────────────────────────────────────────────────────
const TechnicalSupport = ({ emailType }) => {
	const { org, user } = useStore();
	const [isExpanded, setIsExpanded] = useState(false);
	const [showReportPopUp, setShowReportPopUp] = useState(false);
	const [showConfirmationPopUp, setShowConfirmationPopUp] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isEmailValid, setIsEmailValid] = useState(true);
	const [isDoctorNameValid, setIsDoctorNameValid] = useState(true);
	const [isNameValid, setIsNameValid] = useState(true);
	const [isPatientCommentValid, setIsPatientCommentValid] = useState(true);
	const [isInquiryTypeValid, setIsInquiryTypeValid] = useState(true);
	const [providers, setProviders] = useState([]);
	const [email, setEmail] = useState('');
	const [name, setName] = useState('');
	const [doctorName, setDoctorName] = useState('');
	const [inquiryType, setInquiryType] = useState("");
	const [patientComment, setPatientComment] = useState('');
	const [attachments, setAttachments] = useState(null);
	const [errorMessage, setErrorMessage] = useState('');
	const [showCustomAlert, setShowCustomAlert] = useState(false);

	const fileInputRef = useRef(null);

	const isDoctor = user.roles && user.roles[0] === "doctor";
	const isPatient = !user.roles;
	const isContactClinictEmail = emailType === 'contact_clinic';
	const isTechSupportEmail = emailType === 'tech_support';
	const hasClinicEmail = isContactClinictEmail && org?.communicationSetting?.email;

	useEffect(() => {
		injectFont();
		injectStyles();
	}, []);

	const getOrgProviders = async () => {
		let orgId;
		if (isPatient) {
			const session = JSON.parse(localStorage.getItem('session'));
			orgId = session.org;
		} else {
			orgId = user.default_org;
		}

		const fetchOrgProviders = await api(
			apiConfigs.baseOption("get", "appointments/getOrgProviders", {
				params: { org_id: orgId, limit: -1 },
			})
		);

		if (fetchOrgProviders && fetchOrgProviders.status === 200) {
			const filteredProviders = fetchOrgProviders.data.data.data.filter((doctor) => {
				if (doctor.modes) {
					return doctor.modes.includes("001") || doctor.modes.includes("002");
				} else {
					return false;
				}
			});
			return filteredProviders;
		} else {
			return fetchOrgProviders.status;
		}
	};

	const getNumIssues = () => {
		let issues = 0;

		const isValidName = /^[a-zA-Z ]{2,30}$/.test(name);
		setIsNameValid(isValidName);

		const isValidEmail = /^\S+@\S+\.\S+$/.test(email);
		setIsEmailValid(isValidEmail);

		if (!isValidName || !isValidEmail) issues++;

		if (!isDoctor) {
			const isValidDoctorName = !!doctorName;
			setIsDoctorNameValid(isValidDoctorName);
			if (!isValidDoctorName) issues++;
		}

		if (isContactClinictEmail) {
			const isValidInquiryType = !!inquiryType;
			setIsInquiryTypeValid(isValidInquiryType);
			if (!isValidInquiryType) issues++;
		}

		const isValidPatientComment = !!patientComment && /[0-9a-zA-Z!@#$%^&*]{1,300}/.test(patientComment);
		setIsPatientCommentValid(isValidPatientComment);
		if (!isValidPatientComment) issues++;

		return issues;
	};

	const getFileAttachment = () => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			if (attachments === null) {
				resolve([]);
			}
			reader.readAsDataURL(attachments);
			reader.addEventListener('load', (event) => {
				if (event?.target?.result) {
					resolve([{ path: event.target.result }]);
				}
				reject(new Error('Error while reading file'));
			});
		});
	};

	const handleShowSendToTechSupportPopUp = () => setShowReportPopUp(!showReportPopUp);

	const clearFilelds = () => {
		setIsLoading(false);
		setEmail('');
		setName('');
		setPatientComment('');
		setDoctorName('');
		setAttachments(null);
		isContactClinictEmail && setInquiryType('');
		if (fileInputRef && fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleSendEmailToSupport = async (e) => {
		e.preventDefault();
		let numIssues = getNumIssues();
		if (numIssues > 0) return;

		setIsLoading(true);

		const attachment = !!attachments ? await getFileAttachment() : null;

		const body = {
			data: {
				org: { logo_link: undefined, id: undefined, name: undefined },
				attachments: attachment,
				doctorName,
				patientComment,
				[isPatient ? 'patientEmail' : 'email']: email,
				[isPatient ? 'patientName' : 'name']: name,
				userType: user.roles ? user.roles[0] : 'patient',
				emailType,
			},
		};

		if (isContactClinictEmail) {
			body.data.inquiryType = inquiryType;
			body.data.sendTo = decrypt(org.communicationSetting.email);
			body.data.org.logo_link = org.logo_link;
			body.data.org.id = org.id;
			body.data.org.name = org.name;
		} else if (isTechSupportEmail) {
			body.data.sendTo = 'support.services@portonhealth.com';
			body.data.org.logo_link = org.logo;
			body.data.org.id = org._id;
			body.data.org.name = org.org_name;
		}

		await api(apiConfigs.baseOption('POST', 'techSupport/sendEmail', body)).then((resp) => {
			if (resp.status === 200) {
				setShowReportPopUp(false);
				setShowConfirmationPopUp(true);
				clearFilelds();
			} else {
				setIsLoading(false);
				setErrorMessage(resp.error.response.data);
				setShowCustomAlert(true);
			}
		});
	};

	const handleAttacnmetsChange = (e) => setAttachments(e.target.files[0]);
	const handleRemoveFile = () => {
		setAttachments(null);
		fileInputRef.current.value = '';
	};

	const handleCancel = () => {
		setShowReportPopUp(false);
		clearFilelds();
	};

	useEffect(() => {
		if (!isDoctor) {
			getOrgProviders()
				.then((res) => {
					if (res.length < 0) {
						console.log("No Providers with Audio or Video call appointments Enabled");
					}
					setProviders(res);
				})
				.catch((e) => {
					console.log(e);
				});
		}
	}, [user]);

	return (
		<>
			{/* ── Collapsible card ── */}
			<div className="ts-wrap">
				<div className="ts-card">
					<div className="ts-card__head" onClick={() => setIsExpanded(!isExpanded)}>
						<div className="ts-card__head-left">
							<div className="ts-card__icon">
								<MessageIcon />
							</div>
							<span className="ts-card__title">
								{isContactClinictEmail ? "Contact Message" : "Technical Support"}
							</span>
						</div>
						<button
							className={`ts-card__chevron${isExpanded ? ' expanded' : ''}`}
							aria-expanded={isExpanded}
							aria-label="show more"
						>
							<ChevronIcon />
						</button>
					</div>

					{isExpanded && (
						<div className="ts-card__body">
							<div className="ts-card__desc">
								{isContactClinictEmail
									? "We know your inquiry is important to you, and we're here to help! If your inquiry is non-urgent or is a general question, please feel free to send us an email. Our support team will promptly get in touch."
									: "We're here to Help! To ensure your experience with our software is seamless, we encourage you to report any issues you encounter using the link below. Our technical support team is dedicated to resolving your concerns."
								}
							</div>
							<button className="ts-card__cta" onClick={handleShowSendToTechSupportPopUp}>
								<MailSendIcon />
								{isContactClinictEmail ? "CONTACT YOUR CLINIC" : "Report an issue"}
							</button>
						</div>
					)}
				</div>
			</div>

			{/* ── Form dialog ── */}
			<Dialog
				open={showReportPopUp}
				PaperProps={{ style: { borderRadius: 18, overflow: 'hidden', maxWidth: 480, width: '100%' } }}
			>
				<div className="ts-wrap">
					<div className="ts-dialog-head">
						<div className="ts-dialog-title">
							{isContactClinictEmail ? "Need Assistance?" : "Report an Issue"}
						</div>
						<div className="ts-dialog-sub">
							{isContactClinictEmail
								? (hasClinicEmail ? "This form is only used for contacting your clinic. Please fill out all the required fields and DO NOT use this form for a medical emergency." : "")
								: "We understand the importance of your inquiry, and our support team is here to assist you. To report any issues use the Form provided below."
							}
						</div>
					</div>

					{(!isContactClinictEmail || hasClinicEmail) ? (
						<div className="ts-dialog-body">
							<StyledField
								id="fullName"
								label="Full Name"
								placeholder="Please Enter Your Full Name"
								value={name}
								error={!isNameValid}
								helperText="Please enter a valid name"
								onChange={(e) => setName(e.target.value)}
							/>
							<StyledField
								id="email"
								label="Email"
								placeholder="Please Enter Your Email"
								error={!isEmailValid}
								helperText="Please enter a valid email address"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
							/>

							{isContactClinictEmail && (
								<StyledField
									id="inquiryType"
									label="Inquiry Type"
									value={inquiryType}
									onChange={(e) => setInquiryType(e.target.value)}
									error={!isInquiryTypeValid}
									helperText="Please enter a valid inquiry type"
									options={
										<>
											<option value="">SELECT INQUIRY TYPE</option>
											{contactInquiryTypes.map(({ key, label }) => (
												<option value={label} key={key}>{label.toUpperCase()}</option>
											))}
										</>
									}
								/>
							)}

							{!isDoctor && (
								<StyledField
									id="doctor"
									label="Doctor"
									value={doctorName}
									onChange={(e) => setDoctorName(e.target.value)}
									error={!isDoctorNameValid}
									helperText="Please provide doctor name"
									options={
										<>
											<option value="">SELECT DOCTOR</option>
											{providers.length > 0 && providers.map(({ _id, contact_name }) => (
												<option value={contact_name} key={_id}>{contact_name.toUpperCase()}</option>
											))}
										</>
									}
								/>
							)}

							<StyledField
								id="describeIssue"
								label="Describe your issue"
								placeholder="Please Describe your issue here"
								multiline
								rows={5}
								maxLength={300}
								showTextCounter
								value={patientComment}
								error={!isPatientCommentValid}
								helperText="Please provide additional comment about the issue"
								onChange={(e) => setPatientComment(e.target.value)}
							/>

							<div className="ts-field">
								<label>Attachment</label>
								<div className="ts-upload-zone">
									<input
										accept="image/*"
										id="contained-button-file"
										type="file"
										onChange={handleAttacnmetsChange}
										ref={fileInputRef}
									/>
									<div className="ts-upload-zone__text">Click to Upload</div>
								</div>
								<div className="ts-upload-hint">Accepted file types are: PNG and JPG</div>

								{attachments && (
									<div className="ts-attached-file">
										<div className="ts-attached-file__left">
											<AttachIcon />
											<span>{attachments.name}</span>
										</div>
										<span className="ts-clear-icon" onClick={handleRemoveFile}>
											<ClearIcon />
										</span>
									</div>
								)}
							</div>
						</div>
					) : (
						<div className="ts-no-email-msg">
							There is no contact email provided from the clinic. Please directly call the clinic for support.
						</div>
					)}

					<DialogActions className="ts-dialog-foot">
						{isLoading ? (
							<div className="ts-loading-wrap">
								<CircularProgress style={{ color: '#E47029' }} size={28} />
							</div>
						) : (
							<>
								<button className="ts-btn-cancel" onClick={handleCancel} disabled={isLoading}>
									CANCEL
								</button>
								{isContactClinictEmail
									? (hasClinicEmail && (
										<button className="ts-btn-send" onClick={handleSendEmailToSupport} disabled={isLoading}>
											SEND TO CLINIC
										</button>
									))
									: (
										<button className="ts-btn-send" onClick={handleSendEmailToSupport} disabled={isLoading}>
											SEND TECH SUPPORT
										</button>
									)
								}
							</>
						)}
					</DialogActions>
				</div>
			</Dialog>

			<ConfirmationDialog
				open={showConfirmationPopUp}
				title="Your email has been received"
				text={<>We are currently reviewing your email.<br />Thank you for your patience.</>}
				closeButtonTitle="OK"
				onClose={() => setShowConfirmationPopUp(false)}
			/>

			<CustomAlert
				description={errorMessage}
				type="error"
				openAlert={showCustomAlert}
				setOpenAlert={setShowCustomAlert}
			/>
		</>
	);
};

export default TechnicalSupport;